import { useSelector } from "react-redux";
import { useMemo } from "react";
import { Paper, Box } from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { styled } from "@mui/material/styles";
import { OVERALL_PAGE_SUB_TYPE, PAGE_TYPE } from "../../../types/pages.type";
import { calcProPercentsBasedOnFilterAndDays, getPositivePlusSign, itemPercentage } from "../../../utilis/common";
import { calculateDiffBetweenPercentages, pickComparisonColor } from "../../../utilis/percentage-comparison";
import CardItemTooltip from "../../shared/CardItemTooltip";
import NoRecordFound from "../../shared/NoRecordFound";
import { HospitalizationsProgressBar } from "../../../features/hospital/components";
import InsuranceCardSkeleton from "./InsuranceCardSkeleton";
import EllipsisTooltipTypography from "../shared/EllipsisTooltipTypography";
import CheckboxLoader from "../../shared/checkbox-loader/CheckboxLoader";
import useLoadingToggle from "../../hooks/useLoadingToggle";
import useMergedData from "../../hooks/useMergedData";

export const InsuranceListItem = styled(ListItem, {
	shouldForwardProp: (prop) => prop !== 'isMobile'
})(({ isMobile }) => ({
	paddingTop: isMobile ? "0px" : 0,
	paddingBottom: isMobile ? "0px" : 0,
	margin: isMobile ? "0px" : "2px 0px 2px 0px",
	...(isMobile && {
		minHeight: "32px",
		height: "32px",
	}),
	"& .MuiListItemIcon-root": {
		minWidth: isMobile ? "24px" : 0,
	},
	"& .MuiButton-root": {
		width: "66px",
		height: "24px",
		borderRadius: "5px",
		fontSize: "10px",
		fontFamily: "manrope-bold",
		"&.selected": {
			background: "#4879F5",
			color: "#FFFFFF !important",
		},
	},
	"& .MuiCheckbox-root": {
		padding: isMobile ? "0px" : 0,
		paddingRight: isMobile ? "2px" : "4px",
	},
	"&.Mui-selected, .Mui-focusVisible": {
		backgroundColor: "#fff",
		"&:before": {
			content: `""`,
			display: "block",
			height: "16px",
			width: "16px",
			backgroundColor: "#4879f5",
			position: "absolute",
			left: "-1px",
			transform: "translateX(-100%)",
			borderRadius: "50%",
			transition: "all 0.2s",
		},
	},
	"& .MuiListItemButton-root": {
		padding: isMobile ? "2px 4px" : "4px 8px",
		...(isMobile && {
			minHeight: "32px",
			height: "32px",
		}),
		"&:hover": {
			backgroundColor: "transparent",
		},
	},
	".insuranceProgressBar": {
		width: isMobile ? "25%" : "65%",
		"& .MuiButtonBase-root": {
			color: "#444652",
			"&:hover": {
				background: "#EEF3F7",
			},
		},
	},
}));

const YesPaper = styled(Paper)`
	&::-webkit-scrollbar {
		background-color: #fff;
		height: 4px;
		width: 5px;
	}
	&::-webkit-scrollbar-track {
		background-color: #fff;
		width: 5px;
	}
	&::-webkit-scrollbar-thumb {
		background-color: #babac0;
		border-radius: 4px;
	}
	
	@media (max-width: 768px) {
		&::-webkit-scrollbar {
			height: 6px;
			width: 6px;
		}
		&::-webkit-scrollbar-thumb {
			background-color: #d0d0d0;
			border-radius: 6px;
		}
		&::-webkit-scrollbar-track {
			background-color: #f5f5f5;
			border-radius: 6px;
		}
	}
`;

const InsuranceCard = ({
	data,
	dataComparison,
	admissionCompareAgainst,
	admissionCompareAgainstComparison,
	averageCensus,
	averageCensusComparison,
	handleToggle,
	handleToggleAll,
	selectedItem = [],
	type,
	page,
	filter,
	filterComparison,
	height,
	isPercentageShow = true,
	totalField = "total",
	cardTitle,
	reverseColors,
	reverseColorsAdmissionPage,
	loading,
	projectionDays,
	isComparingAgainstAvgCensus,
	transferType,
	priorityNumber,
	lockedTotalBy,
	searchValue,
	isCustom = false,
	question,
	isCustomTab = false,
	customTab = null,
	disableInteractions = false,
	isMobile = false,
	hideProgressBar = false,
	/** Compact, height-by-content layout for hospital header filter popover only. */
	headerFilterPopover = false,
}) => {
	const specialPages = [PAGE_TYPE.ADMISSION];
	const isSpecialPage = specialPages.find((x) => x === page)?.length > 0;
	const isFirstItemInPriorityData = priorityNumber === 1;
	const { comparingAgainstDifferentFacility } = useSelector(state => state.comparison);

	const isPlusSign = useMemo(() => {
		return page === PAGE_TYPE.OVERALL && transferType === OVERALL_PAGE_SUB_TYPE.TOTAL;
	}, [page, transferType]);

	const { loadingItems, handleToggleWithLoader } = useLoadingToggle();
	const mergedData = useMergedData(data, dataComparison);

	const paperSx = headerFilterPopover
		? {
			minHeight: 0,
			maxHeight: 320,
			height: "auto",
			overflowY: "auto",
			overflowX: "hidden",
			width: "100%",
			boxShadow: "none",
			backgroundColor: "transparent",
		}
		: {
			minHeight: 350,
			maxHeight: 350,
			height: "auto",
			overflow: mergedData.length > 8 ? "auto" : "visible",
			width: "100%",
			'@media (max-width: 768px)': {
				minHeight: "300px",
				maxHeight: "300px",
				height: "auto",
				overflowX: "hidden",
				overflowY: mergedData.length > 6 ? "auto" : "visible",
				borderRadius: "12px",
				boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
			},
		};

	return (
		<YesPaper sx={paperSx}>
			<List
				sx={{
					width: "100%",
					padding: headerFilterPopover ? 1 : (isMobile ? 0.5 : 1),
					...(height && !headerFilterPopover && { height }),
					...(isMobile && {
						'& .MuiListItem-root': {
							marginBottom: '0px'
						}
					}),
					...(!headerFilterPopover && mergedData.length <= 5 && {
						height: 'auto',
						overflow: 'visible'
					}),
					...(headerFilterPopover && {
						minHeight: 0,
						height: 'auto',
						overflow: 'visible',
					}),
				}}
			>
				{loading ? (
					<InsuranceCardSkeleton />
				) : (
					mergedData.length > 0 &&
					mergedData
						.filter((x) => x.label.toLowerCase().includes((searchValue || "").toLowerCase()))
						.map((item, index) => {
							const ID = item._id;
							const labelId = `checkbox-list-label-${ID}`;
							const selected = selectedItem.indexOf(ID) !== -1;
							let itemComparison = dataComparison?.find((x) => x._id === item._id);
							if (comparingAgainstDifferentFacility) {
								itemComparison = dataComparison?.find((x) => x?.label === item?.label);
							}
							// const itemComparison = dataComparison?.find((x) => x._id === item._id);
							const itemTotal = !isNaN(Number(item.total)) ? Number(item.total) : 0;
							const itemTotalComparison = !isNaN(Number(itemComparison?.[totalField]))
								? Number(itemComparison?.[totalField])
								: 0;
							let comparisonColor;
							let comparingAgainstScaled;
							let numberOfDays;

							if (page === PAGE_TYPE.ADMISSION) {
								const itemTotalTemp = itemPercentage(itemTotal, admissionCompareAgainst, "number");
								const itemTotalComparisonTemp = itemPercentage(
									itemComparison?.total,
									admissionCompareAgainstComparison,
									"number"
								);
								const {
									percentageDiff,
									itemTotalComparisonScaled,
									numberOfDays: numberOfDaysTemp,
								} = calculateDiffBetweenPercentages(
									itemTotalTemp,
									itemTotalComparisonTemp,
									reverseColorsAdmissionPage,
									filter,
									filterComparison,
									itemComparison?.total,
									projectionDays
								);

								numberOfDays = numberOfDaysTemp;
								comparingAgainstScaled = itemTotalComparisonScaled;
								comparisonColor = pickComparisonColor(percentageDiff, isSpecialPage);
							} else {
								const percentageOfAverageCensus = itemPercentage(itemTotal, averageCensus, "number");
								const percentageOfAverageCensusComparison = itemPercentage(
									itemTotalComparison,
									averageCensusComparison,
									"number"
								);
								const {
									percentageDiff,
									itemTotalComparisonScaled,
									numberOfDays: numberOfDaysTemp,
								} = calculateDiffBetweenPercentages(
									percentageOfAverageCensus,
									percentageOfAverageCensusComparison,
									reverseColors,
									filter,
									filterComparison,
									itemComparison?.total,
									projectionDays
								);

								numberOfDays = numberOfDaysTemp;
								comparingAgainstScaled = itemTotalComparisonScaled;
								comparisonColor = pickComparisonColor(percentageDiff);
							}

							return (
								<InsuranceListItem key={ID} disableRipple={true} isMobile={isMobile}>
									<ListItemButton
										role={undefined}
										onClick={() => {
											if (disableInteractions) return;
											handleToggleWithLoader(async () => await handleToggle({
												item,
												type,
												itemComparison,
												isChecked: selected,
												cardTitle,
												question: isCustom
													? { isCustom: true, customQuestionInputType: question?.customQuestionInputType, isCustomTab, cardTitle, customTab }
													: null
											}), item._id)
										}}
										dense={true}
										disableGutters
										disableRipple={true}
									>
										<ListItemIcon>
											<Box position="relative" display="inline-flex" alignItems="center">
												<div style={{
													cursor: item?.isReadOnly ? 'not-allowed' : 'default',
													display: 'inline-flex',
													alignItems: 'center'
												}}>
													<Checkbox
														edge="start"
														checked={selected}
														tabIndex={-1}
														disableRipple
														disabled={item?.isReadOnly || disableInteractions}
														inputProps={{ "aria-labelledby": labelId }}
														sx={{
															...(loadingItems[ID] && { opacity: 0 }),
															...(item?.isReadOnly && {
																'&.MuiCheckbox-root.Mui-disabled': {
																	cursor: 'not-allowed !important',
																	pointerEvents: 'auto !important'
																},
																'&.MuiCheckbox-root.Mui-disabled .MuiSvgIcon-root': {
																	cursor: 'not-allowed !important'
																}
															})
														}}
													/>
												</div>
												<CheckboxLoader isLoading={loadingItems[ID]} />
											</Box>
										</ListItemIcon>
										<CardItemTooltip item={item}>
											<ListItemText
												primaryTypographyProps={{
													variant: "subtitle3",
													noWrap: true,
													sx: {
														...!isMobile && {
															maxWidth: { xs: "85px", lg: "150px", },
															minWidth: { xs: "85px", lg: "150px", },
															width: { xs: "85px", lg: "150px", },
														},
														...isMobile && {
															maxWidth: "100%",
															minWidth: "200px",
															width: "200px",
															fontSize: "12px",
															fontWeight: 500,
															lineHeight: 1.1,
														}
													},
												}}
												disableRipple
												id={labelId}
												primary={<EllipsisTooltipTypography label={item.label} />}
											/>
										</CardItemTooltip>

										<HospitalizationsProgressBar
											hideProgressBar={hideProgressBar}
											comparisonColor={comparisonColor}
											comparingAgainstScaled={isPlusSign ? getPositivePlusSign(comparingAgainstScaled || 0) : comparingAgainstScaled || 0}
											comparingAgainst={itemComparison?.total}
											numberOfDays={numberOfDays}
											className={"insuranceProgressBar"}
											btnBgColor={"#EEF3F7"}
											// selected={selected}
											progressColor={"#92C7FF"}
											progressHeight={calcProPercentsBasedOnFilterAndDays(
												item.percentage,
												filter,
												projectionDays,
												true,
												isComparingAgainstAvgCensus ||
												lockedTotalBy === "census" ||
												(isFirstItemInPriorityData && (!transferType || transferType.length === 0))
											)}
											total={calcProPercentsBasedOnFilterAndDays(item.total, { ...filter, transferType }, projectionDays)}
											percentage={`(${calcProPercentsBasedOnFilterAndDays(
												item.percentage,
												filter,
												projectionDays,
												true,
												isComparingAgainstAvgCensus ||
												lockedTotalBy === "census" ||
												(isFirstItemInPriorityData && (!transferType || transferType.length === 0))
											)}%)`}
											isPercentageShow={isPercentageShow}
											loading={loading}
											isMobile={isMobile}
										/>
									</ListItemButton>
								</InsuranceListItem>
							);
						})
				)}
				<NoRecordFound data={mergedData} />
			</List>
		</YesPaper >
	);
};

export default InsuranceCard;
